import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Navigate, RouteProps } from 'react-router-dom';
import Loading from '$/components/loading/Loading';
import { LoginRedirectService } from '$/services';
import { AppState } from '$/store';
import { getIsCurrentUserAdminState, getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';
import { LayoutRoute } from './LayoutRoute';
import * as Bixby from '$/core/';

export interface IAuthenticatedLayoutRouteProps {
  component: React.ComponentType<any>;
  layout: React.ComponentType<any>;
  isAuthenticated: boolean;
  isLoading: boolean;
  onlyAdmin?: boolean;
  onlySuperAdmin?: boolean;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  featuredFlags?: string[];
  isRefreshModalOpen?: boolean;
  releaseFeatureFlags?: Bixby.Models.ReleaseFeatureFlag[] | undefined;
}

const AuthenticatedLayoutRouteCmpt = ({
  component: Component,
  layout: Layout,
  isAuthenticated,
  isLoading,
  onlyAdmin,
  onlySuperAdmin,
  isAdmin,
  isSuperAdmin,
  featuredFlags,
  isRefreshModalOpen,
  releaseFeatureFlags,
}: IAuthenticatedLayoutRouteProps & RouteProps) => {
  const element = useMemo(() => {
    const loading = (
      <Layout>
        <Loading pageLevel />
      </Layout>
    );
    const layout = <LayoutRoute component={Component} layout={Layout} />;
    const redirectToPortfolio = () => <Navigate to="/portfolio" />;

    if (isAuthenticated) {
      if (featuredFlags === undefined || releaseFeatureFlags === undefined) {
        return loading;
      }

      if (onlySuperAdmin) {
        if (isLoading) {
          return loading;
        } else if (!isSuperAdmin) {
          return redirectToPortfolio();
        }
      } else if (onlyAdmin) {
        if (isLoading) {
          return loading;
        } else if (!isAdmin) {
          return redirectToPortfolio();
        }
      }
      return isLoading ? loading : layout;
    } else if (isRefreshModalOpen) {
      return layout;
    }

    return <Navigate to={LoginRedirectService.buildLoginUrl()} />;
  }, [
    Component,
    Layout,
    featuredFlags,
    isAdmin,
    isAuthenticated,
    isLoading,
    isSuperAdmin,
    onlyAdmin,
    onlySuperAdmin,
    isRefreshModalOpen,
    releaseFeatureFlags,
  ]);

  return element;
};

function mapStateToProps(state: AppState) {
  const { isLoading } = state.loginState;
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  return {
    isAuthenticated: state.loginState.authenticated,
    isLoading,
    isAdmin,
    isSuperAdmin,
    featuredFlags: state.loginState.featureFlags,
    isRefreshModalOpen: state.loginState.isRefreshModalOpen,
    releaseFeatureFlags: state.releaseFeatureFlagsState.data,
  };
}

export const AuthenticatedLayoutRoute: React.ComponentType<
  Pick<
    IAuthenticatedLayoutRouteProps & RouteProps,
    Exclude<keyof IAuthenticatedLayoutRouteProps, 'isAuthenticated' | 'isComplete' | 'isLoading'>
  >
> = connect(mapStateToProps)(AuthenticatedLayoutRouteCmpt);
